import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getMusica, exportMusicaCSV, exportMusicaExcel, deleteMusica, deleteMultipleMusica, assignMusicaImages, refreshMusicaImages, exportMusicaIBS, exportMusicaWeb, exportMusicaWebFiltered, generateMissingMusicaWebDescriptions, populateSelectedMusicaFromDiscogs, getLayoutsByModulo, generatePdf, refreshToken } from '../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Dropdown, Pagination, Card, Button, OverlayTrigger, Popover, Badge, InputGroup, ProgressBar } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faSortUp, faSortDown, faTimes, faMusic, faBoxes, faEuroSign, faPlus, faCog, faSearch, faFileInvoice, faWarehouse, faExclamationTriangle, faStar, faRecycle, faArchive } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import ColumnOrderController from '../common/ColumnOrderController';
import BulkEditMusicaModal from '../common/BulkEditMusicaModal';
import FilterPanel from '../common/FilterPanel';
import SortableHeader from '../common/SortableHeader';
import StatsCards from '../common/StatsCards';
import { usePageTitle } from '../context/PageTitleContext';
import { getOptimalColumnWidth, getMinColumnWidth, autoFitColumns, autoResizeColumn, resetColumnWidths } from '../common/columnSizingUtils';

const columnHelper = createColumnHelper();

// Colonne numeriche per i filtri avanzati
const numericColumns = ['id', 'anno', 'prezzo_negozio', 'prezzo_ibs', 'costo_acquisto', 'quantita', 'iva'];

// Colonne data per i filtri avanzati (Musica non ha campi data)
const dateColumns = [];

const allColumnsMap = {
    id: "ID",
    titolo: "Titolo",
    immagine_url: "URL Immagine",
    anno: "Anno",
    genere: "Genere",
    artista: "Artista",
    descrizione: "Descrizione",
    casa_di_produzione: "Casa di Produzione",
    categoria: "Categoria",
    supporto: "Supporto",
    sku: "SKU",
    barcode: "Barcode",
    barcode_ibs: "Barcode IBS",
    prezzo_negozio: "Prezzo Negozio",
    prezzo_ibs: "Prezzo IBS",
    condizione: "Condizione",
    quantita: "Quantità",
    fuori_catalogo: "Fuori Catalogo",
    iva: "IVA",
    costo_acquisto: "Costo Acquisto",
    descrizione_web: "Descrizione Web",
    ubicazione: "Ubicazione",
    fornitore_id: "ID Fornitore",
    fornitore: "Fornitore",
    da_pubblicare_su_ibs: "Presente su IBS", // Nuovo campo
    in_vetrina: "In Vetrina", // Nuovo campo
};

const initialVisibleColumns = ['immagine_url', 'titolo', 'genere', 'artista', 'anno', 'quantita', 'costo_acquisto', 'fornitore', 'da_pubblicare_su_ibs', 'in_vetrina'];
const visibilityKey = 'table-visibility-musica';

function MusicaList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const navigate = useNavigate();
  const [musica, setMusica] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMusicaIds, setSelectedMusicaIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingIBS, setExportingIBS] = useState(false);
  const [exportingWeb, setExportingWeb] = useState(false);
  const [exportingAllWeb, setExportingAllWeb] = useState(false);
  const [filterPanelKey, setFilterPanelKey] = useState(0);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [populatingDiscogs, setPopulatingDiscogs] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  
  // Stati per il progresso delle operazioni di immagini
  const [imageMusicAssignProgress, setImageMusicAssignProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_product: '',
    status: 'idle',
    success: 0,
    failed: 0,
    failed_report: []
  });
  
  const [imageMusicRefreshProgress, setImageMusicRefreshProgress] = useState({
    active: false,
    processed: 0,
    total: 0,
    current_product: '',
    status: 'idle',
    success: 0,
    failed: 0,
    low_quality: 0,
    improved_online: 0,
    failed_report: [],
    low_quality_report: []
  });
  
  const [distinctValues, setDistinctValues] = useState({
    genere: [], artista: [], anno: [], supporto: [], condizione: [],
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-musica');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-musica');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const allKeys = Object.keys(allColumnsMap);
    const savedOrderJSON = localStorage.getItem('table-column-order-musica');
    
    if (savedOrderJSON) {
      let savedOrder = JSON.parse(savedOrderJSON);
      // Rimuoviamo 'select' per gestirlo a parte
      savedOrder = savedOrder.filter(c => c !== 'select');
      
      // Troviamo le colonne nuove che non sono nell'ordine salvato
      const newKeys = allKeys.filter(key => !savedOrder.includes(key));
      
      // Uniamo l'ordine salvato con le nuove chiavi e aggiungiamo 'select' all'inizio
      const finalOrder = ['select', ...savedOrder, ...newKeys];
      return finalOrder;
    }

    // Se non c'è niente di salvato, usiamo l'ordine di default
    const defaultOrder = ['select', ...allKeys];
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-musica');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-musica', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-musica', JSON.stringify(columnFilters));
  }, [columnFilters]);
  
  useEffect(() => {
    localStorage.setItem('table-column-order-musica', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-musica', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalMusica, setTotalMusica] = useState(0);
  const [reportType, setReportType] = useState('all');
  const [reportTitle] = useState('Tutta la Musica');
  
  // Sticky columns configuration
  const stickyColumns = ['select', 'id'];
  
  const warehouseValue = useMemo(() => {
    if (reportType !== 'in_stock') return 0;
    return musica.reduce((acc, item) => (acc + ((item.costo_acquisto || 0) * (item.quantita || 0))), 0);
  }, [musica, reportType]);

  const fetchMusicaData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = columnFilters.reduce((acc, filter) => {
        const { id, value } = filter;
        
        // Range filter for numbers/dates (array with [min, max])
        if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
          const [min, max] = value;
          
          if (min) {
            acc[`${id}__gte`] = numericColumns.includes(id) ? parseFloat(min) : min;
          }
          if (max) {
            acc[`${id}__lte`] = numericColumns.includes(id) ? parseFloat(max) : max;
          }
        }
        // Multi-select filter
        else if (Array.isArray(value)) {
          if (value.length > 0) acc[id] = { 'in': value };
        } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
          acc[id] = null;
        } else if (value !== undefined && value !== '') {
          acc[id] = value;
        }
        return acc;
      }, {});
      
      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const stringifiedFilters = JSON.stringify(filters);
      
      const response = await getMusica((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, stringifiedFilters);
      setMusica(response.data.musica);
      setTotalMusica(response.data.total);
      if (response.data.facets) {
        setDistinctValues(response.data.facets);
      }
    } catch (err) {
      setError('Errore nel caricamento della musica.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  useEffect(() => {
    fetchMusicaData();
  }, [fetchMusicaData]);

  const getColumnValue = (item, columnName) => {
    if (columnName === 'immagine_url' && item.immagine_url) {
      let imageUrl;
      if (item.immagine_url.startsWith('http')) {
          imageUrl = item.immagine_url;
      } else if (item.immagine_url.startsWith('/images/')) {
          let baseUrl = process.env.REACT_APP_API_URL; // Corretto
          let staticBaseUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;
          imageUrl = `${staticBaseUrl}${item.immagine_url}`;
      } else {
          imageUrl = `${process.env.REACT_APP_API_URL}${item.immagine_url}`; // Corretto
      }
      return <img src={imageUrl} alt={item.titolo} style={{ maxHeight: '35px', maxWidth: '35px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />;
    }
    if (columnName === 'titolo') {
        const titleContent = item.titolo || 'N/A';
        if (item.immagine_url) {
            const popover = (
                <Popover id={`popover-musica-${item.id}`}>
                    <Popover.Body>
                      {(() => {
                          let imageUrl;
                          if (item.immagine_url.startsWith('http')) {
                              imageUrl = item.immagine_url;
                          } else if (item.immagine_url.startsWith('/images/')) {
                              let baseUrl = process.env.REACT_APP_API_URL; // Corretto
                              let staticBaseUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;
                              imageUrl = `${staticBaseUrl}${item.immagine_url}`;
                          } else {
                              imageUrl = `${process.env.REACT_APP_API_URL}${item.immagine_url}`; // Corretto
                          }
                          return <img src={imageUrl} alt={item.titolo} style={{ maxWidth: '200px', height: 'auto' }}/>;
                      })()}
                    </Popover.Body>
                </Popover>
            );
            return (<OverlayTrigger placement="right" overlay={popover} delay={{ show: 250, hide: 400 }}><span>{titleContent}</span></OverlayTrigger>);
        }
        return titleContent;
    }
    if (columnName === 'fornitore' && item.fornitore) return item.fornitore.denominazione;
    if (columnName === 'da_pubblicare_su_ibs') {
        return (
            <Badge bg={item.da_pubblicare_su_ibs ? 'success' : 'danger'}>
                {item.da_pubblicare_su_ibs ? 'Sì' : 'No'}
            </Badge>
        );
    }
    if (columnName === 'in_vetrina') {
        return (
            <Badge bg={item.in_vetrina ? 'success' : 'danger'}>
                {item.in_vetrina ? 'Sì' : 'No'}
            </Badge>
        );
    }
    if (columnName === 'fuori_catalogo') {
        return (
            <Badge bg={item.fuori_catalogo ? 'warning' : 'success'}>
                {item.fuori_catalogo ? 'Fuori Catalogo' : 'In Catalogo'}
            </Badge>
        );
    }
    return item[columnName] != null ? String(item[columnName]) : 'N/A';
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={musica.length === 0} />,
      cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} />,
      minSize: 30,
      maxSize: 30,
      size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
    }),
    ...Object.entries(allColumnsMap).map(([key, value]) => columnHelper.accessor(key, {
      header: value,
      cell: info => getColumnValue(info.row.original, key),
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: ['genere', 'anno', 'supporto', 'condizione', 'prezzo_negozio', 'prezzo_ibs', 'quantita'].includes(key) ? 'select' : (key === 'da_pubblicare_su_ibs' || key === 'in_vetrina' || key === 'fuori_catalogo') ? 'select' : 'text',
        filterOptions: (key === 'da_pubblicare_su_ibs' || key === 'in_vetrina' || key === 'fuori_catalogo') ? [{ value: true, label: 'Sì' }, { value: false, label: 'No' }] : distinctValues[key],
      },
    })),
  ], [musica, distinctValues]);

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: musica, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true, manualSorting: true,
  });

  const handleAutoResize = (column) => {
    autoResizeColumn(column, table);
  };

  const handleAutoFitAllColumns = () => {
    autoFitColumns(table);
  };

  const handleResetColumnWidths = () => {
    resetColumnWidths(table);
  };

  // Column presets
  const columnPresets = {
    essenziale: ['sku', 'titolo', 'anno', 'artista', 'quantita', 'prezzo_negozio', 'prezzo_ibs', 'supporto', 'barcode', 'barcode_ibs', 'condizione', 'fuori_catalogo'],
    scheda_musica: ['sku', 'titolo', 'anno', 'genere', 'artista', 'casa_di_produzione', 'quantita', 'prezzo_negozio', 'supporto', 'condizione'],
    completa: Object.keys(allColumnsMap)
  };

  const applyColumnPreset = (presetName) => {
    const preset = columnPresets[presetName];
    if (preset) {
      const newVisibility = Object.keys(allColumnsMap).reduce((acc, key) => {
        acc[key] = preset.includes(key);
        return acc;
      }, {});
      setColumnVisibility(newVisibility);
      localStorage.setItem(visibilityKey, JSON.stringify(newVisibility));
    }
  };

  // Calcola statistiche per StatsCards
  const stats = useMemo(() => {
    const totalValue = musica.reduce((sum, item) => sum + ((item.prezzo_negozio || 0) * (item.quantita || 0)), 0);
    const skuCount = musica.reduce((sum, item) => sum + (item.quantita || 0), 0);
    
    return [
      { label: 'Totale Musica', value: totalMusica, icon: faMusic, color: 'primary' },
      { label: 'SKU in Stock', value: skuCount, icon: faBoxes, color: 'success' },
      { label: 'Valore Magazzino', value: `€ ${totalValue.toFixed(2)}`, icon: faEuroSign, color: 'warning' },
    ];
  }, [musica, totalMusica]);

  useEffect(() => {
    setSelectedMusicaIds(table.getSelectedRowModel().rows.map(row => row.original.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getSelectedRowModel().rows]);

  // Un clic apre la scheda in visualizzazione, due clic la modifica (stesso meccanismo di FilmList).
  // La selezione delle righe resta sulla casella a sinistra.
  const rowClickTimeoutRef = useRef(null);

  const handleRowClick = (row) => {
    if (rowClickTimeoutRef.current) {
      clearTimeout(rowClickTimeoutRef.current);
      rowClickTimeoutRef.current = null;
      return;
    }
    rowClickTimeoutRef.current = setTimeout(() => {
      navigate(`/musica/${row.original.id}`);
      rowClickTimeoutRef.current = null;
    }, 250);
  };

  const sectionByColumn = {
    ubicazione: 'ubicazione',
    prezzo_negozio_base: 'prezzi',
    prezzo_negozio: 'prezzi',
    prezzo_ibs: 'prezzi',
  };

  const handleRowDoubleClick = (e, row) => {
    const columnId = e.target.closest('td')?.getAttribute('data-column-id');
    const section = sectionByColumn[columnId];
    navigate(`/musica/edit/${row.original.id}${section ? `#${section}` : ''}`);
  };

  const createDownloadLink = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    setExporting(true);
    try {
      const response = await exportMusicaCSV(selectedMusicaIds);
      createDownloadLink(response, 'musica_selezionata.csv');
      table.toggleAllRowsSelected(false);
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExporting(false); }
  };

  const handleExportAllExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportMusicaExcel();
      createDownloadLink(response, 'musica.xlsx');
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExportingExcel(false); }
  };

  const handleExportIBS = async () => {
    setExportingIBS(true);
    try {
      const response = await exportMusicaIBS();
      createDownloadLink(response, 'musica_ibs.csv');
    } catch (err) { setError('Errore durante l\'esportazione per IBS.'); }
    finally { setExportingIBS(false); }
  };

  const handleExportWebSelected = async () => {
    setExportingWeb(true);
    try {
      const response = await exportMusicaWeb(selectedMusicaIds);
      createDownloadLink(response, 'musica_web_selezione.csv');
    } catch (err) { setError('Errore durante l\'esportazione per il sito web.'); }
    finally { setExportingWeb(false); }
  };

  const handleExportAllWeb = async () => {
    setExportingAllWeb(true);
    setError(null);
    try {
      const filters = JSON.stringify(columnFilters.reduce((acc, filter) => {
        const { id, value } = filter;
        if (Array.isArray(value) && value.length > 0) acc[id] = { 'in': value };
        else if (typeof value === 'string' && value.toUpperCase() === 'N/A') acc[id] = null;
        else acc[id] = value;
        return acc;
      }, {}));
      
      const response = await exportMusicaWebFiltered(filters);
      createDownloadLink(response, 'musica_web_filtrato.csv');
    } catch (err) {
      setError('Errore durante l\'esportazione di tutta la musica filtrata per il web.');
      console.error(err);
    } finally {
      setExportingAllWeb(false);
    }
  };

  const handlePrintWithLayout = async (layoutId) => {
    if (selectedMusicaIds.length === 0) return toast.error("Seleziona almeno un elemento per la stampa.");
    setLoading(true);
    try {
      const response = await generatePdf(layoutId, selectedMusicaIds);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento_layout_${layoutId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { setError("Errore durante la generazione del PDF."); }
    finally { setLoading(false); }
  };



  const handleAssignImages = async () => {
    // Conferma elaborazione di TUTTI gli album senza immagine
    const confirmMessage = 
      `🔍 ASSEGNAZIONE IMMAGINI MANCANTI\n\n` +
      `Questa funzione elaborerà TUTTI gli album senza immagine nel database.\n\n` +
      `Per ogni album verrà cercata l'immagine:\n` +
      `1️⃣ Prima in LOCALE (cartella immagini_ean_locale) per barcode\n` +
      `2️⃣ Se non trovata, cerca su Discogs per artista + titolo\n\n` +
      `⏱️ Il processo potrebbe richiedere diversi minuti.\n` +
      `📊 Alla fine verrà mostrato un report completo con:\n` +
      `   • Immagini assegnate con successo\n` +
      `   • Album con problemi (con dettagli e report scaricabile)\n\n` +
      `Procedere con l'elaborazione di TUTTI gli album?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Usa un numero molto grande per elaborare tutti gli album
    const numProducts = 999999;
    
    // Streaming con progress bar
    if (!window.confirm(`Verrà mostrato il progresso in tempo reale. Procedere?`)) return;
    
    // Ottieni un token fresco prima di avviare lo streaming
    let token;
    try {
      const tokenResponse = await refreshToken();
      token = tokenResponse.data.access_token;
      localStorage.setItem('access_token', token);
    } catch (err) {
      toast.error('Impossibile aggiornare il token. Effettua nuovamente il login.');
      return;
    }
    
    const params = new URLSearchParams({
      max_products: numProducts,
      token: token
    });
    
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/musica/assign-images-stream/?${params.toString()}`;
    
    setImageMusicAssignProgress({ ...imageMusicAssignProgress, active: true, status: 'running', processed: 0, total: 0 });
    
    const es = new EventSource(url);
    
    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'start') {
        setImageMusicAssignProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.type === 'progress') {
        setImageMusicAssignProgress(prev => ({
          ...prev,
          processed: data.processed,
          current_product: data.product_title,
          status: 'running',
          success: data.status === 'success' ? prev.success + 1 : prev.success,
          failed: data.status === 'failure' ? prev.failed + 1 : prev.failed
        }));
      } else if (data.type === 'done') {
        setImageMusicAssignProgress(prev => ({ 
          ...prev, 
          status: 'success', 
          current_product: 'Completato!', 
          success: data.success, 
          failed: data.failed,
          failed_report: data.failed_report || []
        }));
        fetchMusicaData();
        
        let message = `Completato!\n\nSuccessi: ${data.success}\nFalliti: ${data.failed}`;
        if (data.failed_report && data.failed_report.length > 0) {
          message += `\n\n⚠️ Ci sono ${data.failed_report.length} album con errori.\nPuoi scaricare il report dettagliato cliccando sul bottone che apparirà sotto la progress bar.`;
        }
        toast.success(message);
        
        es.close();
      }
    };
    
    es.onerror = (err) => {
      console.error("Errore EventSource:", err);
      setImageMusicAssignProgress(prev => ({ ...prev, status: 'error', current_product: 'Errore di connessione.' }));
      es.close();
      setTimeout(() => {
        setImageMusicAssignProgress({ active: false, processed: 0, total: 0, current_product: '', status: 'idle', success: 0, failed: 0 });
      }, 5000);
    };
  };

  const handleRefreshImages = async () => {
    // Scelta strategia con spiegazione completa
    const strategiaScelta = prompt(
      `🔍 AGGIORNAMENTO QUALITÀ IMMAGINI\n\n` +
      `Questa funzione elaborerà TUTTE le immagini esistenti nel database.\n\n` +
      `Per ogni immagine verrà verificata la qualità (dimensioni, peso).\n` +
      `Se trovo immagini locali con qualità scarsa (< 400x400px), cosa devo fare?\n\n` +
      `Digita "ONLINE" = Cerca versione migliore su Discogs (ma potrebbe essere generica)\n` +
      `Digita "LOCALE" = Usa comunque locale e segnala nel report (CONSIGLIATO)\n\n` +
      `Annulla = Interrompi operazione`,
      'LOCALE'
    );
    
    if (!strategiaScelta) return; // Annullato
    
    const useOnline = strategiaScelta.toUpperCase().includes('ONLINE');
    
    // Conferma finale con riepilogo
    const confirmMessage = 
      `📊 CONFERMA ELABORAZIONE:\n\n` +
      `✅ Verranno elaborate TUTTE le immagini esistenti\n` +
      `✅ Strategia immagini scarse: ${useOnline ? 'Cerca ONLINE (Discogs)' : 'Usa LOCALE + report'}\n\n` +
      `⏱️ Il processo potrebbe richiedere diversi minuti.\n` +
      `📋 Alla fine riceverai un report completo con:\n` +
      `   • Immagini con qualità OK\n` +
      `   • Immagini di qualità BASSA (con report scaricabile)\n` +
      `   • Immagini migliorate da online\n\n` +
      `Procedere?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Usa un numero molto grande per elaborare tutte le immagini
    const numProducts = 999999;
    
    // Ottieni un token fresco prima di avviare lo streaming
    let token;
    try {
      const tokenResponse = await refreshToken();
      token = tokenResponse.data.access_token;
      localStorage.setItem('access_token', token);
    } catch (err) {
      toast.error('Impossibile aggiornare il token. Effettua nuovamente il login.');
      return;
    }
    
    const params = new URLSearchParams({
      max_products: numProducts,
      use_online_fallback: useOnline,
      token: token
    });
    
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/musica/refresh-images-stream/?${params.toString()}`;
    
    setImageMusicRefreshProgress({ ...imageMusicRefreshProgress, active: true, status: 'running', processed: 0, total: 0 });
    
    const es = new EventSource(url);
    
    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'start') {
        setImageMusicRefreshProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.type === 'progress') {
        setImageMusicRefreshProgress(prev => ({
          ...prev,
          processed: data.processed,
          current_product: data.product_title,
          status: 'running',
          success: data.status === 'success' || data.status === 'warning' ? prev.success + 1 : prev.success,
          failed: data.status === 'failure' ? prev.failed + 1 : prev.failed,
          low_quality: data.status === 'warning' ? prev.low_quality + 1 : prev.low_quality
        }));
      } else if (data.type === 'done') {
        setImageMusicRefreshProgress(prev => ({ 
          ...prev, 
          status: 'success', 
          current_product: 'Completato!', 
          success: data.success, 
          failed: data.failed,
          low_quality: data.low_quality || 0,
          improved_online: data.improved_online || 0,
          failed_report: data.failed_report || [],
          low_quality_report: data.low_quality_report || []
        }));
        fetchMusicaData();
        
        let message = `Completato!\n\n` +
          `Successi: ${data.success}\n` +
          `⚠️ Qualità bassa: ${data.low_quality || 0}\n` +
          `Migliorate da online: ${data.improved_online || 0}\n` +
          `Falliti: ${data.failed}`;
        
        if ((data.low_quality_report && data.low_quality_report.length > 0) || (data.failed_report && data.failed_report.length > 0)) {
          message += `\n\n⚠️ Ci sono problemi da risolvere.\nPuoi scaricare i report dettagliati cliccando sui bottoni che appariranno sotto la progress bar.`;
        }
        
        toast.success(message);
        es.close();
      }
    };
    
    es.onerror = (err) => {
      console.error("Errore EventSource:", err);
      setImageMusicRefreshProgress(prev => ({ ...prev, status: 'error', current_product: 'Errore di connessione.' }));
      es.close();
      setTimeout(() => {
        setImageMusicRefreshProgress({ active: false, processed: 0, total: 0, current_product: '', status: 'idle', success: 0, failed: 0, low_quality: 0, improved_online: 0 });
      }, 5000);
    };
  };

  // Funzioni helper per download CSV reports
  const downloadFailedMusicAssignReport = () => {
    if (!imageMusicAssignProgress.failed_report || imageMusicAssignProgress.failed_report.length === 0) {
      toast.info('Nessun report di errori disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Cercato Locale', 'Cercato Discogs', 'Motivo errore'],
      ...imageMusicAssignProgress.failed_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.searched_local || 'N/A',
        item.searched_discogs || 'N/A',
        item.reason
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `musica_immagini_assegnazione_fallite_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadFailedMusicRefreshReport = () => {
    if (!imageMusicRefreshProgress.failed_report || imageMusicRefreshProgress.failed_report.length === 0) {
      toast.info('Nessun report di errori disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Motivo errore'],
      ...imageMusicRefreshProgress.failed_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.reason
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `musica_immagini_refresh_fallite_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const downloadLowQualityMusicReport = () => {
    if (!imageMusicRefreshProgress.low_quality_report || imageMusicRefreshProgress.low_quality_report.length === 0) {
      toast.info('Nessun report di bassa qualità disponibile.');
      return;
    }
    
    const csvContent = [
      ['ID', 'SKU', 'Titolo', 'Barcode', 'Larghezza (px)', 'Altezza (px)', 'Peso (KB)', 'Problemi', 'File Sorgente'],
      ...imageMusicRefreshProgress.low_quality_report.map(item => [
        item.id,
        item.sku,
        item.titolo,
        item.barcode,
        item.width,
        item.height,
        item.file_size_kb,
        item.issues,
        item.source_file
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `musica_immagini_bassa_qualita_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const closeImageMusicAssignProgress = () => {
    setImageMusicAssignProgress({ 
      active: false, 
      processed: 0, 
      total: 0, 
      current_product: '', 
      status: 'idle', 
      success: 0, 
      failed: 0,
      failed_report: []
    });
  };

  const closeImageMusicRefreshProgress = () => {
    setImageMusicRefreshProgress({ 
      active: false, 
      processed: 0, 
      total: 0, 
      current_product: '', 
      status: 'idle', 
      success: 0, 
      failed: 0, 
      low_quality: 0, 
      improved_online: 0,
      failed_report: [],
      low_quality_report: []
    });
  };



  const handlePopulateSelectedMusicaFromDiscogs = async () => {
    if (selectedMusicaIds.length === 0 || !window.confirm('Popolare i dati per gli album selezionati da Discogs?')) return;
    setPopulatingDiscogs(true);
    try {
      const response = await populateSelectedMusicaFromDiscogs(selectedMusicaIds);
      setLastReport(response.data);
      toast.info(`Processo completato: ${response.data.updated_musica.length} aggiornati, ${response.data.failed_musica.length} errori.`);
      fetchMusicaData();
    } catch (err) { setError('Errore durante la popolazione dei dati da Discogs.'); }
    finally { setPopulatingDiscogs(false); }
  };

  const handleGenerateDescriptions = async () => {
    if (!window.confirm('Sei sicuro di voler generare le descrizioni mancanti per il web?')) return;
    setLoading(true);
    try {
      const response = await generateMissingMusicaWebDescriptions();
      toast.success(response.data.message || 'Descrizioni generate con successo');
      fetchMusicaData();
    } catch (err) { 
      setError('Errore durante la generazione delle descrizioni web.'); 
      console.error(err);
    }
    finally { setLoading(false); }
  };

  // Alias per compatibilità con actionsDropdownContent
  const handleAutoAssignImages = handleAssignImages;
  const handlePopulateFromDiscogs = handlePopulateSelectedMusicaFromDiscogs;

  const handleDownloadErrorReport = () => {
      if (!lastReport?.failed_musica?.length) return toast.error("Nessun dato di errore da esportare.");
      const headers = ["ID Album", "Titolo", "Motivo del Fallimento"];
      const csvRows = [headers.join(';'), ...lastReport.failed_musica.map(item => [item.id, `"${item.titolo}"`, `"${item.reason}"`].join(';'))];
      const blob = new Blob(["\uFEFF" + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'report_errori_musica.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };
  
  const filterFields = useMemo(() => [
    { id: '_global_search', label: 'Cerca', type: 'text', placeholder: 'Titolo, artista, barcode...', colSize: 4 },
    { id: 'genere', label: 'Genere', type: 'select', options: distinctValues.genere, colSize: 2 },
    { id: 'artista', label: 'Artista', type: 'select', options: distinctValues.artista, colSize: 2 },
    { id: 'supporto', label: 'Supporto', type: 'select', options: distinctValues.supporto, colSize: 2 },
    { id: 'condizione', label: 'Condizione', type: 'select', options: distinctValues.condizione, colSize: 2 },
  ], [distinctValues]);

  const handleApplyFilters = useCallback((filterValues) => {
    const newColumnFilters = Object.entries(filterValues)
      .filter(([, v]) => v !== undefined && v !== '' && v !== null)
      .map(([id, value]) => ({ id, value }));
    setColumnFilters(newColumnFilters);
    setCurrentPage(1);
  }, []);

  const handleResetFilters = () => {
    setReportType('all');
    table.resetColumnFilters();
    setSorting([]);
    setFilterPanelKey(k => k + 1);
  };

  const deleteApiMap = { musica: deleteMusica };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalMusica / itemsPerPage);
    const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
    if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
    else {
      if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
      else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
      else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return pageNumbers;
  };

  // Usiamo SortableHeader dal componente comune invece di definirlo localmente

  const hasManualFilters = table.getState().columnFilters.some(f => !(reportType !== 'all' && f.id === 'quantita'));

  // Conta i filtri attivi per il badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    // Filtri colonna
    table.getState().columnFilters.forEach(f => {
      if (!(reportType !== 'all' && f.id === 'quantita')) {
        count++;
      }
    });
    return count;
  }, [table, reportType]);

  // Funzione per renderizzare card musica mobile
  const renderMusicaCard = useCallback((album, isSelected, onToggleSelect) => {
    return (
      <Row>
        <Col xs={2} className="d-flex align-items-center justify-content-center">
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            style={{ transform: 'scale(1.3)' }}
          />
        </Col>
        <Col xs={3}>
          {album.immagine_url ? (
            <img 
              src={album.immagine_url} 
              alt={album.titolo}
              style={{ 
                width: '100%', 
                maxWidth: '70px',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{ 
              width: '70px', 
              height: '70px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              color: '#6c757d',
              textAlign: 'center'
            }}>
              <FontAwesomeIcon icon={faMusic} size="2x" />
            </div>
          )}
        </Col>
        <Col xs={7}>
          <div className="mb-1">
            <strong style={{ fontSize: '0.95rem', color: '#212529' }}>
              {album.titolo}
            </strong>
          </div>
          
          {album.artista && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '4px' }}>
              <strong>Artista:</strong> {album.artista}
            </div>
          )}
          
          {album.genere && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '4px' }}>
              <strong>Genere:</strong> {album.genere}
            </div>
          )}

          {album.supporto && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '6px' }}>
              <strong>Supporto:</strong> {album.supporto}
            </div>
          )}

          <div className="d-flex flex-wrap" style={{ gap: '4px', marginTop: '8px' }}>
            {album.quantita !== undefined && (
              <Badge bg={album.quantita > 0 ? 'success' : 'danger'} style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faBoxes} className="me-1" />
                Qta: {album.quantita}
              </Badge>
            )}
            
            {album.prezzo_negozio && (
              <Badge bg="primary" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faEuroSign} className="me-1" />
                €{album.prezzo_negozio}
              </Badge>
            )}
            
            {album.anno && (
              <Badge bg="info" style={{ fontSize: '0.65rem' }}>
                {album.anno}
              </Badge>
            )}
          </div>
        </Col>
      </Row>
    );
  }, []);

  // Configurazione filtri rapidi per mobile
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faMusic,
      isActive: reportType === 'all',
      onClick: () => setReportType('all'),
      variant: 'outline-primary',
      gradientActive: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      color: '#007bff',
      borderColorActive: '#007bff'
    },
    {
      label: 'Magazzino',
      icon: faWarehouse,
      isActive: reportType === 'in_stock',
      onClick: () => setReportType('in_stock'),
      variant: 'outline-success',
      gradientActive: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
      color: '#28a745',
      borderColorActive: '#28a745'
    },
    {
      label: 'Esauriti',
      icon: faExclamationTriangle,
      isActive: reportType === 'out_of_stock',
      onClick: () => setReportType('out_of_stock'),
      variant: 'outline-danger',
      gradientActive: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
      color: '#dc3545',
      borderColorActive: '#dc3545'
    }
  ], [reportType]);

  // Contenuto dropdown azioni per mobile
  const actionsDropdownContent = useMemo(() => (
    <>
      <Dropdown.Header>Esporta</Dropdown.Header>
      <Dropdown.Item onClick={handleExportSelected} disabled={selectedMusicaIds.length === 0 || exporting}>
        {exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>
        {exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportIBS} disabled={exportingIBS}>
        {exportingIBS ? 'Esportazione...' : 'Esporta per IBS'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllWeb} disabled={exportingAllWeb}>
        {exportingAllWeb ? 'Esportazione...' : 'Esporta per Web (Tutti Filtrati)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportWebSelected} disabled={selectedMusicaIds.length === 0 || exportingWeb}>
        {exportingWeb ? 'Esportazione...' : 'Esporta per Web (Selezione)'}
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Header>Utilità</Dropdown.Header>
      <Dropdown.Item onClick={handleAutoAssignImages}>
        Auto-assegna Immagini
      </Dropdown.Item>
      <Dropdown.Item onClick={handleGenerateDescriptions}>
        Genera Descrizioni Web Mancanti
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePopulateFromDiscogs} disabled={selectedMusicaIds.length === 0 || populatingDiscogs}>
        {populatingDiscogs ? 'Popolazione in corso...' : 'Popola da Discogs'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleDownloadErrorReport} disabled={!lastReport || !lastReport.failed_albums || lastReport.failed_albums.length === 0}>
        Scarica Report Errori Discogs
      </Dropdown.Item>
    </>
  ), [exporting, exportingExcel, exportingIBS, exportingWeb, exportingAllWeb, populatingDiscogs, lastReport, selectedMusicaIds.length, handleExportSelected, handleExportAllExcel, handleExportIBS, handleExportAllWeb, handleExportWebSelected, handleAutoAssignImages, handleGenerateDescriptions, handlePopulateFromDiscogs, handleDownloadErrorReport]);

  usePageTitle('Musica');

  return (
    <>
      <StatsCards stats={stats} />

    <Card>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          // Mobile: componente riutilizzabile
          <MobileListLayout
            entityName="album"
            entityNamePlural="album"
            createPath="/musica/nuovo"
            createButtonText="Nuova Musica"
            createButtonIcon={faPlus}
            actionsDropdownContent={actionsDropdownContent}
            quickFilters={quickFilters}
            searchPlaceholder="Cerca musica..."
            activeFiltersCount={activeFiltersCount}
            onSearchChange={(e) => {
              const val = e.target.value;
              setColumnFilters(prev => {
                const without = prev.filter(f => f.id !== '_global_search');
                return val ? [...without, { id: '_global_search', value: val }] : without;
              });
            }}
            searchValue={columnFilters.find(f => f.id === '_global_search')?.value || ''}
            items={table.getRowModel().rows.map(row => row.original)}
            selectedIds={selectedMusicaIds}
            renderCard={renderMusicaCard}
            onCardClick={(album) => navigate(`/musica/${album.id}`)}
            onToggleSelect={(id) => {
              const row = table.getRowModel().rows.find(r => r.original.id === id);
              if (row) row.toggleSelected();
            }}
            onDeleteMultiple={async (ids) => {
              await deleteMultipleMusica(ids);
              fetchMusicaData();
              setSelectedMusicaIds([]);
            }}
            deleteConfirmMessage={(count) => `Eliminare ${count} album?`}
            extraStickyActions={
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowBulkEditModal(true)}
                style={{ borderRadius: '8px', fontWeight: '600' }}
              >
                <FontAwesomeIcon icon={faCog} className="me-1" />
                Modifica
              </Button>
            }
          />
        ) : (
          // Desktop: Layout originale
          <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/musica/nuovo" className="me-2" size="sm" variant="success">Nuova Musica</Button>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="primary" id="dropdown-import-musica" size="sm">Importa</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/nuovo/musica/import">Importa da CSV/Excel</Dropdown.Item>
                <Dropdown.Item as={Link} to="/musica/nuovo">Importa da Discogs</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="info" id="dropdown-export" size="sm">Esporta</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleExportSelected} disabled={selectedMusicaIds.length === 0 || exporting}>{exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}</Dropdown.Item>
                <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>{exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}</Dropdown.Item>
                <Dropdown.Item onClick={handleExportIBS} disabled={exportingIBS}>{exportingIBS ? 'Esportazione...' : 'Esporta per IBS'}</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleExportAllWeb} disabled={exportingAllWeb}>
                  {exportingAllWeb ? 'Esportazione...' : 'Esporta per Web (Tutti Filtrati)'}
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportWebSelected} disabled={selectedMusicaIds.length === 0 || exportingWeb}>{exportingWeb ? 'Esportazione...' : 'Esporta per Web (Selezione)'}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handlePrintWithLayout(1)} disabled={selectedMusicaIds.length === 0}>Stampa con Layout</Dropdown.Item>
                <Dropdown.Item onClick={handleAssignImages} disabled={loading}>Assegna Immagini</Dropdown.Item>
                <Dropdown.Item onClick={handleRefreshImages} disabled={loading}>🔄 Aggiorna Qualità Immagini Esistenti</Dropdown.Item>
                <Dropdown.Item onClick={handlePopulateSelectedMusicaFromDiscogs} disabled={selectedMusicaIds.length === 0 || populatingDiscogs}>{populatingDiscogs ? 'In corso...' : 'Popola da Discogs'}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Form.Group className="me-2">
              <Form.Select onChange={(e) => setReportType(e.target.value)} value={reportType} size="sm">
                <option value="all">Tutta la Musica</option>
                <option value="in_stock">In Magazzino</option>
                <option value="out_of_stock">Esauriti</option>
              </Form.Select>
            </Form.Group>

            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-viste" size="sm">
                Viste
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => applyColumnPreset('essenziale')}>
                  📋 Vista Essenziale
                </Dropdown.Item>
                <Dropdown.Item onClick={() => applyColumnPreset('scheda_musica')}>
                  🎵 Vista Scheda Musica
                </Dropdown.Item>
                <Dropdown.Item onClick={() => applyColumnPreset('completa')}>
                  📊 Vista Completa
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="outline-secondary"
              size="sm"
              className="ms-2"
              onClick={handleAutoFitAllColumns}
              title="Adatta tutte le colonne al contenuto"
            >
              Auto-Fit
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="ms-2"
              onClick={handleResetColumnWidths}
              title="Ripristina larghezze predefinite"
            >
              Reset Colonne
            </Button>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters && reportType === 'all' && sorting.length === 0}>Azzera Filtri</Button>
            <Button variant="outline-danger" size="sm" className="ms-2" onClick={handleDownloadErrorReport} disabled={!lastReport?.failed_musica?.length}>Scarica Report Errori</Button>
          </Col>
        </Row>
        )}
      </Card.Header>
      <Card.Body>
        <FilterPanel key={filterPanelKey} fields={filterFields} onApply={handleApplyFilters} />
        {imageMusicAssignProgress.active && (
          <div className="mb-3">
            <ProgressBar 
              animated={imageMusicAssignProgress.status !== 'success'} 
              now={imageMusicAssignProgress.total > 0 ? (imageMusicAssignProgress.processed / imageMusicAssignProgress.total) * 100 : 0} 
              label={`${imageMusicAssignProgress.processed}/${imageMusicAssignProgress.total} - ${imageMusicAssignProgress.current_product}`} 
              className="mb-2" 
              variant={imageMusicAssignProgress.status === 'error' ? 'danger' : imageMusicAssignProgress.status === 'success' ? 'success' : 'info'}
            />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <strong>Assegnazione Immagini Musica:</strong> {imageMusicAssignProgress.current_product}
              </small>
              <div>
                <Badge bg="success" className="me-1">✅ {imageMusicAssignProgress.success}</Badge>
                <Badge bg="danger">❌ {imageMusicAssignProgress.failed}</Badge>
              </div>
            </div>
            {imageMusicAssignProgress.status === 'success' && (
              <div className="d-flex gap-2">
                {imageMusicAssignProgress.failed_report && imageMusicAssignProgress.failed_report.length > 0 && (
                  <Button size="sm" variant="warning" onClick={downloadFailedMusicAssignReport}>
                    📥 Scarica Report Errori ({imageMusicAssignProgress.failed_report.length})
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={closeImageMusicAssignProgress}>
                  Chiudi
                </Button>
              </div>
            )}
          </div>
        )}
        {imageMusicRefreshProgress.active && (
          <div className="mb-3">
            <ProgressBar 
              animated={imageMusicRefreshProgress.status !== 'success'} 
              now={imageMusicRefreshProgress.total > 0 ? (imageMusicRefreshProgress.processed / imageMusicRefreshProgress.total) * 100 : 0} 
              label={`${imageMusicRefreshProgress.processed}/${imageMusicRefreshProgress.total} - ${imageMusicRefreshProgress.current_product}`} 
              className="mb-2" 
              variant={imageMusicRefreshProgress.status === 'error' ? 'danger' : imageMusicRefreshProgress.status === 'success' ? 'success' : 'info'}
            />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">
                <strong>Aggiornamento Qualità Musica:</strong> {imageMusicRefreshProgress.current_product}
              </small>
              <div>
                <Badge bg="success" className="me-1">✅ {imageMusicRefreshProgress.success}</Badge>
                <Badge bg="warning" className="me-1">⚠️ {imageMusicRefreshProgress.low_quality}</Badge>
                <Badge bg="info" className="me-1">🌐 {imageMusicRefreshProgress.improved_online}</Badge>
                <Badge bg="danger">❌ {imageMusicRefreshProgress.failed}</Badge>
              </div>
            </div>
            {imageMusicRefreshProgress.status === 'success' && (
              <div className="d-flex gap-2">
                {imageMusicRefreshProgress.low_quality_report && imageMusicRefreshProgress.low_quality_report.length > 0 && (
                  <Button size="sm" variant="warning" onClick={downloadLowQualityMusicReport}>
                    📥 Scarica Report Bassa Qualità ({imageMusicRefreshProgress.low_quality_report.length})
                  </Button>
                )}
                {imageMusicRefreshProgress.failed_report && imageMusicRefreshProgress.failed_report.length > 0 && (
                  <Button size="sm" variant="danger" onClick={downloadFailedMusicRefreshReport}>
                    📥 Scarica Report Errori ({imageMusicRefreshProgress.failed_report.length})
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={closeImageMusicRefreshProgress}>
                  Chiudi
                </Button>
              </div>
            )}
          </div>
        )}
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !isMobile && (
            // Vista Desktop con Tabella
            <div style={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
              <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <React.Fragment key={headerGroup.id}>
                      <tr>
                        {headerGroup.headers.map(header => (
                          <SortableHeader
                            key={header.id}
                            header={header}
                            table={table}
                            sorting={sorting}
                            setSorting={setSorting}
                            handleAutoResize={handleAutoResize}
                            stickyColumns={stickyColumns}
                          />
                        ))}
                      </tr>

                    </React.Fragment>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} onClick={() => handleRowClick(row)} onDoubleClick={(e) => handleRowDoubleClick(e, row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} data-column-id={cell.column.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
        )}
        
        {/* Action Toolbar - Desktop version */}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedMusicaIds} entityType="musica" onDeleteSuccess={fetchMusicaData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleMusica} onBulkEdit={() => setShowBulkEditModal(true)} />
        )}
      </Card.Body>
      {!isMobile && (
        <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalMusica / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalMusica / itemsPerPage))} disabled={currentPage === Math.ceil(totalMusica / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
      )}
    </Card>
    <BulkEditMusicaModal
      show={showBulkEditModal}
      onHide={() => setShowBulkEditModal(false)}
      selectedIds={selectedMusicaIds}
      onSuccess={() => {
        fetchMusicaData();
        table.toggleAllRowsSelected(false);
        setShowBulkEditModal(false);
      }}
    />
  </>
  );
}

export default MusicaList;
